import React, { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import authService from '../services/authService'
import { useTranslation } from '../hooks/use-i18n'

interface ResetPasswordForm {
  password: string
  confirmPassword: string
}

const ResetPassword: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordForm>()

  const password = watch('password')

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) {
      toast.error(t('toast.invalidResetLink'))
      return
    }
    try {
      setIsLoading(true)
      await authService.resetPassword(token, data.password)
      setSuccess(true)
      toast.success(t('toast.passwordResetSuccess'))
      setTimeout(() => navigate('/login'), 2000)
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.passwordResetFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-8 pb-12 px-4">
        <div className="blue-sky-card rounded-3xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('auth.resetPassword.invalidLinkTitle')}</h2>
          <p className="text-white/80 mb-6">{t('auth.resetPassword.invalidLinkBody')}</p>
          <Link to="/forgot-password" className="text-sky-400 hover:text-sky-300 underline">
            {t('auth.resetPassword.requestNewLink')}
          </Link>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-8 pb-12 px-4">
        <div className="blue-sky-card rounded-3xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('auth.resetPassword.successTitle')}</h2>
          <p className="text-white/80 mb-6">{t('auth.resetPassword.successBody')}</p>
          <Link to="/login" className="text-sky-400 hover:text-sky-300 underline">
            {t('auth.resetPassword.goToSignIn')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-8 pb-12 lg:pt-12 lg:pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md lg:max-w-lg w-full space-y-8">
        <div className="blue-sky-card rounded-3xl p-8 lg:p-10 xl:p-12 shadow-2xl">
          <div className="text-center mb-8 lg:mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2 lg:mb-3">
              {t('auth.resetPassword.title')}
            </h2>
            <p className="text-white/80 text-base lg:text-lg">
              {t('auth.resetPassword.subtitle')}
            </p>
          </div>

          <form className="space-y-6 lg:space-y-8" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="password" className="block text-sm lg:text-base font-medium text-white/90 mb-2 lg:mb-3">
                {t('auth.resetPassword.newPasswordLabel')}
              </label>
              <input
                {...register('password', {
                  required: t('auth.validation.passwordRequired'),
                  minLength: {
                    value: 6,
                    message: t('auth.validation.passwordMinLength'),
                  },
                })}
                type="password"
                className="w-full px-4 lg:px-5 py-3 lg:py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300 text-sm lg:text-base"
                placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
              />
              {errors.password && (
                <p className="mt-2 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm lg:text-base font-medium text-white/90 mb-2 lg:mb-3">
                {t('auth.resetPassword.confirmPasswordLabel')}
              </label>
              <input
                {...register('confirmPassword', {
                  required: t('auth.validation.confirmPasswordRequired'),
                  validate: (value) => value === password || t('auth.validation.passwordsDoNotMatch'),
                })}
                type="password"
                className="w-full px-4 lg:px-5 py-3 lg:py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300 text-sm lg:text-base"
                placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
              />
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-400">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white py-3 lg:py-4 px-4 lg:px-6 rounded-xl font-semibold text-base lg:text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
            >
              {isLoading ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
            </button>
          </form>

          <div className="mt-6 lg:mt-8 text-center">
            <Link
              to="/login"
              className="text-sm lg:text-base text-white/80 hover:text-white transition-colors hover:underline"
            >
              {t('auth.resetPassword.backToSignIn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
